export const CANVAS_CLIPBOARD_KIND = {
  TEMPLATE_NODE: "template-node",
  IMAGE_LAYER: "image-layer",
};

export const CANVAS_CLIPBOARD_STORAGE_KEY = "bz.canvasClipboard.v1";
export const CANVAS_CLIPBOARD_STORAGE_VERSION = 1;
export const CANVAS_CLIPBOARD_TTL_MS = 2 * 60 * 60 * 1000;

let sessionClipboard = null;
let templateEditorClipboard = null;

const NON_TEXT_INPUT_TYPES = new Set([
  "button",
  "checkbox",
  "color",
  "file",
  "hidden",
  "image",
  "radio",
  "range",
  "reset",
  "submit",
]);

export const isNativeEditableTarget = (target) => {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tagName = target.tagName;
  if (tagName === "TEXTAREA" || tagName === "SELECT") return true;
  if (tagName !== "INPUT") return false;
  const type = (target.getAttribute("type") || "text").toLowerCase();
  return !NON_TEXT_INPUT_TYPES.has(type);
};

export const blurActiveEditable = (event) => {
  if (!event) return;
  if (isNativeEditableTarget(event?.target)) return;
  const active = document.activeElement;
  if (!isNativeEditableTarget(active)) return;
  active.blur();
};

const hasCopyPasteModifier = (event) =>
  (event.ctrlKey || event.metaKey) && !event.altKey && !event.shiftKey;

export const isCopyHotkey = (event) =>
  hasCopyPasteModifier(event) &&
  (event.code === "KeyC" || event.key?.toLowerCase() === "c");

export const isPasteHotkey = (event) =>
  hasCopyPasteModifier(event) &&
  (event.code === "KeyV" || event.key?.toLowerCase() === "v");

export const isGroupHotkey = (event) =>
  hasCopyPasteModifier(event) &&
  (event.code === "KeyG" || event.key?.toLowerCase() === "g");

export const isDeleteHotkey = (event) =>
  !event.repeat &&
  !event.ctrlKey &&
  !event.metaKey &&
  !event.altKey &&
  !event.shiftKey &&
  (event.code === "Delete" ||
    event.code === "Backspace" ||
    event.key === "Delete" ||
    event.key === "Backspace");

const isClipboardKind = (kind) =>
  kind === CANVAS_CLIPBOARD_KIND.TEMPLATE_NODE;

const getLocalStorage = () => {
  try {
    return globalThis.localStorage || null;
  } catch {
    return null;
  }
};

const isFreshCopiedAt = (copiedAt) => {
  const timestamp = Number(copiedAt) || 0;
  if (!timestamp) return false;
  return Date.now() - timestamp <= CANVAS_CLIPBOARD_TTL_MS;
};

const payloadForStorage = (payload) => {
  // eslint-disable-next-line
  const { pasteCount, copiedAt, ...rest } = payload || {};
  return rest;
};

const clearStoredClipboard = () => {
  try {
    getLocalStorage()?.removeItem(CANVAS_CLIPBOARD_STORAGE_KEY);
  } catch {
    // ignore quota / private mode
  }
};

const readStoredClipboard = () => {
  const storage = getLocalStorage();
  if (!storage) return null;
  try {
    const raw = storage.getItem(CANVAS_CLIPBOARD_STORAGE_KEY);
    if (!raw) return null;
    const envelope = JSON.parse(raw);
    if (envelope?.version !== CANVAS_CLIPBOARD_STORAGE_VERSION) {
      storage.removeItem(CANVAS_CLIPBOARD_STORAGE_KEY);
      return null;
    }
    if (!isFreshCopiedAt(envelope.copiedAt)) {
      storage.removeItem(CANVAS_CLIPBOARD_STORAGE_KEY);
      return null;
    }
    const payload = envelope.payload;
    if (!payload || !isClipboardKind(payload.kind)) {
      storage.removeItem(CANVAS_CLIPBOARD_STORAGE_KEY);
      return null;
    }
    return {
      payload: payloadForStorage(payload),
      copiedAt: Number(envelope.copiedAt),
    };
  } catch {
    try {
      storage.removeItem(CANVAS_CLIPBOARD_STORAGE_KEY);
    } catch {
      // ignore quota / private mode
    }
    return null;
  }
};

const writeStoredClipboard = ({ payload, copiedAt }) => {
  const storage = getLocalStorage();
  if (!storage) return false;
  try {
    storage.setItem(
      CANVAS_CLIPBOARD_STORAGE_KEY,
      JSON.stringify({
        version: CANVAS_CLIPBOARD_STORAGE_VERSION,
        copiedAt,
        payload: payloadForStorage(payload),
      })
    );
    return true;
  } catch {
    return false;
  }
};

const readClipboardSlot = (slot) => {
  if (slot && isFreshCopiedAt(slot.copiedAt)) return slot;
  return null;
};

export const setCanvasClipboard = (payload, { persist = false } = {}) => {
  if (!payload) {
    if (persist) {
      templateEditorClipboard = null;
      clearStoredClipboard();
      return;
    }
    sessionClipboard = null;
    return;
  }
  const nextPayload = payloadForStorage(payload);
  const copiedAt = Date.now();
  const nextClipboard = {
    ...nextPayload,
    pasteCount: 0,
    copiedAt,
  };
  if (persist) {
    templateEditorClipboard = nextClipboard;
    writeStoredClipboard({ payload: nextPayload, copiedAt });
    return;
  }
  sessionClipboard = nextClipboard;
};

export const getCanvasClipboard = ({ persist = false } = {}) => {
  if (!persist) {
    const current = readClipboardSlot(sessionClipboard);
    if (!current) sessionClipboard = null;
    return current;
  }
  const stored = readStoredClipboard();
  if (stored) {
    if (templateEditorClipboard?.copiedAt === stored.copiedAt) {
      return templateEditorClipboard;
    }
    templateEditorClipboard = {
      ...stored.payload,
      pasteCount: 0,
      copiedAt: stored.copiedAt,
    };
    return templateEditorClipboard;
  }
  const current = readClipboardSlot(templateEditorClipboard);
  if (!current) templateEditorClipboard = null;
  return current;
};

export const consumeClipboardPasteCount = ({ persist = false } = {}) => {
  const current = getCanvasClipboard({ persist });
  if (!current) return 0;
  const pasteCount = (Number(current.pasteCount) || 0) + 1;
  const next = { ...current, pasteCount };
  if (persist) templateEditorClipboard = next;
  else sessionClipboard = next;
  return pasteCount;
};
